import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eraser, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CleanupRules } from "@/components/admin/CleanupRules";
import { DeleteDomainUsers } from "@/components/admin/DeleteDomainUsers";
import { AutoCleanupToggle } from "@/components/admin/AutoCleanupToggle";

export const TestCleanup = ({ onDone }) => {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [staff, setStaff] = useState(false);
  const [picked, setPicked] = useState([]);
  const toggle = (id) => setPicked((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const loadHistory = () => api.get("/admin/test-accounts/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const load = async () => {
    setBusy(true);
    try {
      const d = (await api.get("/admin/test-accounts/preview")).data;
      setPreview(d); setPicked(d.accounts.map((u) => u.id)); setConfirm(""); setStaff(false);
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-accounts/purge", { confirm, include_staff: staff, user_ids: picked });
      toast.success(`Deleted ${data.deleted} test account${data.deleted === 1 ? "" : "s"} and ${data.leftover_total} leftover record${data.leftover_total === 1 ? "" : "s"}${data.staff_total ? `, plus ${data.staff_total} staff test records` : ""}`);
      setPreview(null); setConfirm(""); onDone?.(); loadHistory();
    } catch (e) { toast.error(errMsg(e, "Cleanup failed")); } finally { setBusy(false); }
  };

  return (
    <div className="mm-card p-5 mt-6" data-testid="admin-test-cleanup">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><Eraser size={18} /> Clear test accounts</div>
          <div className="text-sm text-mm-muted">Accounts matching the test email rules below. Admins and your own account are never included.</div>
        </div>
        <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" disabled={busy} onClick={load} data-testid="admin-cleanup-preview">
          {busy && !preview && <Loader2 size={14} className="animate-spin" />} Preview
        </button>
      </div>
      <DeleteDomainUsers onDone={() => { onDone?.(); loadHistory(); if (preview) load(); }} />
      {preview && (
        <div className="mt-4" data-testid="admin-cleanup-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold" data-testid="admin-cleanup-count">{preview.count} test account{preview.count === 1 ? "" : "s"} found · {picked.length} ticked</div>
            {preview.count > 0 && (
              <div className="flex gap-2 text-xs">
                <button className="text-mm-primary" onClick={() => setPicked(preview.accounts.map((u) => u.id))} data-testid="admin-cleanup-tick-all">Tick all</button>
                <button className="text-mm-muted" onClick={() => setPicked([])} data-testid="admin-cleanup-tick-none">Untick all</button>
              </div>
            )}
          </div>
          <div className="text-sm font-semibold mt-1" data-testid="admin-cleanup-leftover-total">Leftover records from deleted accounts: {preview.leftover_total}</div>
          {preview.leftover_total > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="admin-cleanup-leftovers">
              {Object.entries(preview.leftovers).map(([k, n]) => <span key={k} className="mm-chip !cursor-default !py-1 !px-2.5 !text-[11px]" data-testid={`admin-cleanup-leftover-${k}`}>{k.replace(/_/g, " ")} · {n}</span>)}
            </div>
          )}
          {preview.count > 0 && (
              <div className="max-h-48 overflow-y-auto mm-soft p-3 mt-2 text-xs space-y-0.5" data-testid="admin-cleanup-list">
                {preview.accounts.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 py-0.5 cursor-pointer" data-testid={`admin-cleanup-account-${u.id}`}>
                    <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`admin-cleanup-pick-${u.id}`} />
                    <span className="truncate flex-1">{u.email} · {u.name} <span className="text-mm-muted">({u.rule})</span></span>
                    {u.real_payment && <span className="shrink-0 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold" data-testid={`admin-cleanup-paid-${u.id}`}>paid</span>}
                  </label>
                ))}
                {preview.count > preview.accounts.length && <div className="text-mm-muted">…and {preview.count - preview.accounts.length} more</div>}
              </div>
          )}
          {preview.staff_total > 0 && (
            <label className="mm-soft p-3 mt-3 flex items-start gap-2.5 text-sm cursor-pointer" data-testid="admin-cleanup-staff">
              <input type="checkbox" className="mt-1" checked={staff} onChange={(e) => setStaff(e.target.checked)} data-testid="admin-cleanup-staff-toggle" />
              <span>
                <b>Also wipe staff test data ({preview.staff_total} records)</b>
                <span className="block text-xs text-mm-muted">Notes, check-ins, journals, supporters and payments of {preview.staff_emails.join(" and ")} (the accounts stay so you can sign in), plus sandbox payments.</span>
                <span className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(preview.staff).map(([k, n]) => <span key={k} className="mm-chip !cursor-default !py-0.5 !px-2 !text-[11px]" data-testid={`admin-cleanup-staff-${k}`}>{k.replace(/_/g, " ")} · {n}</span>)}
                </span>
              </span>
            </label>
          )}
          {(preview.count > 0 || preview.leftover_total > 0 || preview.staff_total > 0) && (
              <div className="flex flex-wrap gap-2 mt-3">
                <input className="mm-input flex-1 min-w-[160px]" placeholder='Type DELETE to confirm' value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="admin-cleanup-confirm" />
                <button className="mm-btn !bg-mm-error inline-flex items-center gap-2" disabled={busy || confirm !== "DELETE"} onClick={purge} data-testid="admin-cleanup-delete">
                  {busy && <Loader2 size={14} className="animate-spin" />} Delete ticked and their data
                </button>
              </div>
          )}
          <p className="text-[11px] text-mm-muted mt-2">Payment records are always kept for bookkeeping.</p>
        </div>
      )}
      <AutoCleanupToggle />
      <CleanupRules onChange={() => preview && load()} />
      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t" data-testid="admin-cleanup-history">
          <div className="text-xs font-semibold text-mm-muted mb-2">Cleanup history</div>
          <div className="space-y-1 text-xs">
            {history.map((h, i) => (
              <div key={`${h.at}-${h.kind || h.admin_email}`} className="flex flex-wrap justify-between gap-x-3" data-testid={`admin-cleanup-history-${i}`}>
                <span>{new Date(h.at.endsWith("Z") ? h.at : `${h.at}Z`).toLocaleString()} · {h.kind === "auto" ? <b className="text-mm-primary">Auto</b> : h.admin_email}</span>
                <span className="text-mm-muted flex items-center gap-2"><span>{h.accounts} account{h.accounts === 1 ? "" : "s"} · {h.records} records{h.leftovers ? ` · ${h.leftovers} leftovers` : ""}{h.staff_records ? ` · ${h.staff_records} staff test` : ""}</span>
                  <UndoCell h={h} i={i} onDone={() => { loadHistory(); onDone?.(); }} /></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const UndoCell = ({ h, i, onDone }) => {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  if (h.undone_at) return <span className="mm-chip !text-[11px] !py-0.5 pointer-events-none" title={`Undone by ${h.undone_by || "admin"} on ${new Date(h.undone_at.endsWith("Z") ? h.undone_at : `${h.undone_at}Z`).toLocaleString()}`} data-testid={`admin-cleanup-undone-${i}`}>Undone · {h.undone_by || "admin"}</span>;
  if (!h.can_undo) return null;
  const undo = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/test-accounts/undo/${h.id}`); toast.success(`Restored ${data.accounts} account${data.accounts === 1 ? "" : "s"} and ${data.restored_total - data.accounts} records`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setAsk(false); }
  };
  const left = Math.max(0, Math.ceil((new Date(h.undo_until.endsWith("Z") ? h.undo_until : `${h.undo_until}Z`) - Date.now()) / 86400000));
  return ask
    ? <span className="flex items-center gap-1"><button onClick={undo} disabled={busy} className="font-semibold text-mm-error" data-testid={`admin-cleanup-undo-confirm-${i}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : "Restore everything?"}</button><button onClick={() => setAsk(false)} className="text-mm-muted" data-testid={`admin-cleanup-undo-cancel-${i}`}>Cancel</button></span>
    : <button onClick={() => setAsk(true)} className="font-semibold text-mm-primary underline" title={`${left} day${left === 1 ? "" : "s"} left to undo`} data-testid={`admin-cleanup-undo-${i}`}>Undo ({left}d left)</button>;
};
